// frontend/src/components/settings/ResolveGateDialog.jsx
//
// Generic ACTION-MODEL Resolve wizard for an Exact ERP connector parked at
// RESOLVE. Drives the item gate from a single ``config``: the backend cascade
// suggests a match per staged row, the merchant reviews per-row ACTIONS
//
//   link   — confirm the proposed (or manually picked) pairing
//   import — create the row in Manexys (new-in-Exact; defaults ON, toggleable)
//   skip   — leave it; it reappears on the next sync
//
// and a "Match to…" picker pairs any unmatched/ambiguous row manually.
// Confirming applies all actions, clears the gate, and activates the connector
// once every blocking gate is cleared.
//
// The supplier, location and PO gates are NOT driven by this component: the
// supplier gate is a selection-model interaction (include + merge-on-pair, no
// link/import/skip, no picker) with its own ExactSupplierResolveDialog, the
// location gate is one dropdown per warehouse in ExactLocationResolveDialog,
// and the PO gate has its own dispositions in ExactPoResolveDialog.
//
// ``config`` shape — see ExactItemResolveDialog, its one instance:
//   rowsKey                 data[rowsKey] → the staged rows
//   matchIdField/NameField   row field holding the matched Manexys id/name
//   actionIdField            payload key for a link target ('item_id')
//   signalLabelKeys          { matched_on → i18n key } for the "· <signal>" chip suffix
//   columns                  [{ header: i18nKey, render: (r) => node }] entity columns
//   searchOption             { id: (o)→id, label: (o, isShopify)→string } picker option mapping
//   pickerSubtitle           (r) → string shown above the picker
//   useSelection/useConfirm  the TanStack hooks for this gate
//   useSearch(term, {skip})  the picker's suggestions: { suggestions, loading, failed }
//   useSearchCopy()          the picker box's { label, placeholder }
//   text                     i18n key strings + help/summary builder fns (see below)

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '../../hooks/useFormatters';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import DialogErrorBanner from '../common/DialogErrorBanner';

import { useIsShopify } from '../../contexts/AuthContext';
import { translateRequestError } from '../../queries/api';
import TypeaheadSearchField from '../common/TypeaheadSearchField';

export default function ResolveGateDialog({ config, connectorId, open, onClose, onConfirmed }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const isShopify = useIsShopify();
  const { data, isPending, error } = config.useSelection(connectorId, open);
  const confirmMut = config.useConfirm(connectorId);

  // Per-row action overrides on top of the backend defaults, keyed by
  // connectorId so re-opening starts clean without an effect. Each value is
  // { action: 'link'|'import'|'skip', refId?, refName? } — refId/refName are
  // the gate-agnostic link target (mapped to actionIdField only at confirm).
  const [overrides, setOverrides] = useState({ key: null, map: {} });
  const [filter, setFilter] = useState('all'); // 'all' | 'attention'
  const [confirmError, setConfirmError] = useState(null);
  // The row whose "Match to…" picker is open (null = closed).
  const [pickerRow, setPickerRow] = useState(null);
  const [pickerTerm, setPickerTerm] = useState('');
  const search = config.useSearch(pickerTerm, { skip: !pickerRow });
  const searchCopy = config.useSearchCopy();
  const optionLabel = (o) => config.searchOption.label(o, isShopify);

  const { rowsKey, matchIdField: idF, matchNameField: nameF, actionIdField: actF } = config;
  // No rows until the selection has loaded; once it has, the view always
  // sends them.
  const rows = useMemo(() => (data ? data[rowsKey] : []), [data, rowsKey]);

  const isMatched = (r) => !!r[idF];

  const ov = overrides.key === connectorId ? overrides.map : {};
  // Effective action of a row = merchant override, else the backend's
  // SoR-aware default ('link' for matched, 'import' for new, 'skip' rest).
  const actionOf = (r) => {
    if (ov[r.erp_id]) return ov[r.erp_id];
    if (r.default_action === 'link') {
      return { action: 'link', refId: r[idF], refName: r[nameF] };
    }
    return { action: r.default_action };
  };

  const setOverride = (id, value) => setOverrides((prev) => {
    const map = prev.key === connectorId ? { ...prev.map } : {};
    map[id] = value;
    return { key: connectorId, map };
  });

  // Checkbox semantics: matched / manually-paired rows toggle link↔skip;
  // new-in-Exact rows toggle import↔skip; rows with neither a match nor a
  // usable code have nothing to toggle (picker only).
  const toggle = (r) => {
    const cur = actionOf(r);
    if (cur.action === 'link' || cur.action === 'import') {
      setOverride(r.erp_id, { action: 'skip' });
    } else if (cur.refId || r[idF]) {
      setOverride(r.erp_id, {
        action: 'link',
        refId: cur.refId || r[idF],
        refName: cur.refName || r[nameF],
      });
    } else if (r.import_candidate) {
      setOverride(r.erp_id, { action: 'import' });
    }
  };
  const isTicked = (r) => actionOf(r).action !== 'skip';
  const canTick = (r) => isMatched(r) || r.import_candidate || !!actionOf(r).refId;

  const attentionRows = rows.filter((r) => !isMatched(r));
  const visible = filter === 'attention' ? attentionRows : rows;

  const matchedRows = rows.filter(isMatched);
  const importCandidates = rows.filter((r) => !isMatched(r) && r.import_candidate);
  const linkCount = rows.filter((r) => actionOf(r).action === 'link').length;
  const importCount = rows.filter((r) => actionOf(r).action === 'import').length;

  const linkedOnCount = matchedRows.filter((r) => actionOf(r).action === 'link').length;
  const allMatchedOn = matchedRows.length > 0 && linkedOnCount === matchedRows.length;
  const importsOnCount = importCandidates.filter((r) => actionOf(r).action === 'import').length;
  const allImportsOn = importCandidates.length > 0 && importsOnCount === importCandidates.length;

  const bulkSet = (rowsToSet, makeAction) => setOverrides((prev) => {
    const map = prev.key === connectorId ? { ...prev.map } : {};
    rowsToSet.forEach((r) => { map[r.erp_id] = makeAction(r); });
    return { key: connectorId, map };
  });
  const toggleAllMatched = () => bulkSet(matchedRows, (r) => (allMatchedOn
    ? { action: 'skip' }
    : { action: 'link', refId: r[idF], refName: r[nameF] }));
  const toggleAllImports = () => bulkSet(importCandidates, () => (allImportsOn
    ? { action: 'skip' }
    : { action: 'import' }));

  const handlePick = (row, picked) => {
    setOverride(row.erp_id, {
      action: 'link',
      refId: config.searchOption.id(picked),
      refName: optionLabel(picked),
    });
    setPickerRow(null);
  };

  const handleConfirm = async () => {
    setConfirmError(null);
    // Only send link/import — the backend counts absent rows as skipped.
    const actions = {};
    for (const r of rows) {
      const a = actionOf(r);
      if (a.action === 'link') actions[r.erp_id] = { action: 'link', [actF]: a.refId };
      else if (a.action === 'import') actions[r.erp_id] = { action: 'import' };
    }
    try {
      const resp = await confirmMut.mutateAsync(actions);
      onConfirmed(resp.summary);
      onClose();
    } catch (err) {
      setConfirmError(translateRequestError(err, t, config.text.confirmFailed));
    }
  };

  const matchChip = (r) => {
    const a = actionOf(r);
    if (r.match_confidence === 'linked') {
      return <Chip size="small" color="success" label={t('connectors.resolveAlreadyLinked')} />;
    }
    if (a.action === 'link' && !isMatched(r)) {
      // Manually paired via the picker.
      return <Chip size="small" color="info" label={t(config.text.matchedTo, { name: a.refName })} />;
    }
    if (isMatched(r)) {
      const base = t(config.text.matchedTo, { name: r[nameF] });
      const signal = t(config.signalLabelKeys[r.matched_on]);
      return <Chip size="small" color="info" label={`${base} · ${signal}`} />;
    }
    if (a.action === 'import') {
      return <Chip size="small" color="success" variant="outlined" label={t(config.text.willImport)} />;
    }
    return (
      <Chip size="small" color="warning" variant="outlined" label={t(config.text.reasons[r.unmatched_reason])} />
    );
  };

  return (
    <Dialog open={open} onClose={onClose} className="dialog-form dialog-form--wide">
      <DialogTitle>{t(config.text.title)}</DialogTitle>
      <DialogContent>
        {data && (
          <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
            {config.text.help(t, data)}
          </p>
        )}

        {isPending ? (
          <div style={{ padding: 24, textAlign: 'center' }}><CircularProgress size={24} /></div>
        ) : error ? (
          <Alert severity="error">{translateRequestError(error, t, 'connectors.resolveLoadFailed')}</Alert>
        ) : rows.length === 0 ? (
          <Alert severity="info">
            {data.gate_pending
              ? t(config.text.empty)
              : t(config.text.emptyConfirmed)}
          </Alert>
        ) : (
          <>
            <Alert severity="info">{config.text.summary(t, data)}</Alert>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={allMatchedOn}
                      indeterminate={linkedOnCount > 0 && !allMatchedOn}
                      onChange={toggleAllMatched}
                      size="small"
                      disabled={matchedRows.length === 0}
                    />
                  )}
                  label={t(config.text.selectAllMatched, { count: linkedOnCount, total: matchedRows.length })}
                />
                {importCandidates.length > 0 && (
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={allImportsOn}
                        indeterminate={importsOnCount > 0 && !allImportsOn}
                        onChange={toggleAllImports}
                        size="small"
                      />
                    )}
                    label={t(config.text.importAllNew, { count: importsOnCount, total: importCandidates.length })}
                  />
                )}
              </div>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={filter}
                onChange={(_e, v) => v && setFilter(v)}
              >
                <ToggleButton value="all">{t('connectors.itemFilterAll')}</ToggleButton>
                <ToggleButton value="attention">
                  {t('connectors.itemFilterAttention', { count: attentionRows.length })}
                </ToggleButton>
              </ToggleButtonGroup>
            </div>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  {config.columns.map((c) => <TableCell key={c.header}>{t(c.header)}</TableCell>)}
                  <TableCell>{t(config.text.colMatch)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((r) => (
                  <TableRow key={r.erp_id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isTicked(r)}
                        onChange={() => toggle(r)}
                        disabled={!canTick(r)}
                        size="small"
                      />
                    </TableCell>
                    {config.columns.map((c) => <TableCell key={c.header}>{c.render(r, t, fmt)}</TableCell>)}
                    <TableCell>{matchChip(r)}</TableCell>
                    <TableCell align="right">
                      {!isMatched(r) && r.match_confidence !== 'linked' && (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => { setPickerRow(r); setPickerTerm(''); }}
                        >
                          {t('connectors.itemActionMatchTo')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        <DialogErrorBanner error={confirmError} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={confirmMut.isPending}>
          {t('btn.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isPending || rows.length === 0}
          loading={confirmMut.isPending}
        >
          {importCount > 0
            ? t(config.text.confirmWithImport, { linked: linkCount, imported: importCount })
            : t(config.text.confirm, { count: linkCount })}
        </Button>
      </DialogActions>

      {/* "Match to…" picker for one unmatched Exact row. */}
      <Dialog className="dialog-form" open={!!pickerRow} onClose={() => setPickerRow(null)}>
        <DialogTitle>{t(config.text.pickerTitle)}</DialogTitle>
        <DialogContent>
          {pickerRow && (
            <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
              {config.pickerSubtitle(pickerRow)}
            </p>
          )}
          <TypeaheadSearchField
            autoFocus
            label={searchCopy.label}
            placeholder={searchCopy.placeholder}
            value={pickerTerm}
            onChange={setPickerTerm}
            suggestions={search.suggestions}
            loading={search.loading}
            failed={search.failed}
            getOptionKey={config.searchOption.id}
            renderOption={optionLabel}
            onPick={(picked) => handlePick(pickerRow, picked)}
            emptyText={t(config.text.pickerNoResults)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerRow(null)}>{t('btn.cancel')}</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
